import { useRef, useState } from "react";
import { deleteAccount } from "../services/userService";
import { getUserToken } from "../utils/tokenUtils";
import { getApiErrorMessage } from "../utils/Util";
import { useLanguage } from "../context/LanguageContext";

interface Props {
  onLogout: () => void;
}

function DeleteAccountPopup({ onLogout }: Props) {
  const { t } = useLanguage();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleDelete = async () => {
    const token = getUserToken();
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      await deleteAccount(token.userId);
      closeRef.current?.click();
      onLogout();
    } catch (err) {
      setError(getApiErrorMessage(err, t("deleteAccount.error")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal fade"
      id="deleteAccountModal"
      tabIndex={-1}
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "380px" }}>
        <div className="modal-content rounded-4 shadow">
          <div className="modal-body px-4 py-4 text-center">
            <button ref={closeRef} className="d-none" data-bs-dismiss="modal" />
            <div className="mb-3">
              <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: "3rem" }}></i>
            </div>

            <h6 className="fw-bold fs-5 mb-2">{t("deleteAccount.title")}</h6>
            <p className="text-muted small mb-1">
              {t("deleteAccount.confirm")}
            </p>
            <p className="text-danger small fw-semibold mb-4">
              {t("deleteAccount.irreversible")}
            </p>

            {error && (
              <div className="alert alert-danger py-2 small mb-3">
                <i className="bi bi-x-circle me-1"></i>
                {error}
              </div>
            )}

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary flex-grow-1 rounded-pill"
                data-bs-dismiss="modal"
                disabled={loading}
              >
                {t("deleteAccount.no")}
              </button>
              <button
                type="button"
                className="btn btn-danger flex-grow-1 rounded-pill"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading
                  ? <span className="spinner-border spinner-border-sm me-1" />
                  : <i className="bi bi-trash3 me-1"></i>
                }
                {t("deleteAccount.yes")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeleteAccountPopup;
